import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * Session for turksletloose.com visitors.
 *
 * Deliberately separate from AuthContext: that one speaks to /api/auth/me and
 * refuses anyone outside the guild roster, which is the wrong door for a
 * supporter. A visitor here holds a `tll_home` cookie and need not be a member
 * of anything — signing in only proves which Discord account is buying.
 *
 * Nothing here is an entitlement check. `supporter` is a read of Whop's own
 * lifecycle for display; the perks themselves are granted by Whop's Discord app.
 */

export interface HomeUser {
  discord: { id: string; name: string; username: string; avatar_url: string }
  // The SteamID64 never leaves the backend: staff read it in the dashboard, the
  // visitor gets the account name that identifies the same link.
  steam: { linked: boolean; name: string; verified: boolean }
  supporter: {
    status: string
    active: boolean
    renews_at: number | string | null
    cancel_at_period_end: boolean
  } | null
}

interface HomeAuthValue {
  user: HomeUser | null
  loading: boolean
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const HomeAuthContext = createContext<HomeAuthValue | null>(null)

export const HOME_LOGIN_URL = '/api/home/auth/login'

export function HomeAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<HomeUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/home/me', { credentials: 'include' })
      // 401 is the ordinary state for an anonymous visitor, not a failure.
      setUser(res.ok ? await res.json() : null)
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  const logout = useCallback(async () => {
    try {
      await fetch('/api/home/auth/logout', { method: 'POST', credentials: 'include' })
    } finally {
      setUser(null)
    }
  }, [])

  return (
    <HomeAuthContext.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </HomeAuthContext.Provider>
  )
}

export function useHomeAuth() {
  const ctx = useContext(HomeAuthContext)
  if (!ctx) throw new Error('useHomeAuth must be used inside HomeAuthProvider')
  return ctx
}
